"use client";

import { Card, CircularProgress } from "@mui/material";
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import MDBox from "@/components/atoms/MDBox";
import PageContainer from "@/components/atoms/PageContainer";
import AlertBlock from "@/components/molecules/AlertBlock";
import Suspense from "@/components/molecules/Suspense";
import OrderForm from "@/components/templates/OrderForm";
import { getExpectedOrderAmount, getOrderItemTotals } from "@/components/templates/OrderForm/components/ItemsTab/form-data";
import { normalizeCreation, normalizeUpdate } from "@/components/templates/OrderForm/form-data";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import SWR_KEYS from "@/constants/swr-keys";
import useAuth from "@/contexts/auth-context";
import useLanguage from "@/contexts/language-context";
import useToast from "@/contexts/toast-context";
import useHandleOrderStatusChange from "@/hooks/requests/order-status/use-handle-order-status-change";
import useCreateOrder from "@/hooks/requests/orders/use-create-order";
import useGetOrderById from "@/hooks/requests/orders/use-get-order-by-id";
import useRemoveItem from "@/hooks/requests/orders/use-remove-item-orders";
import useUpdateOrder from "@/hooks/requests/orders/use-update-order";
import useRemovePayment from "@/hooks/requests/orders/user-remove-paymets-orders";
import useLoading from "@/hooks/use-loading";
import useMutate from "@/hooks/use-mutate";
import { IOrderItem, IOrderPayment } from "@/interfaces/order.interfaces";
import { OrderFormPageMessages } from "@/messages/order-form-page/order-form-page.messages";
import { OrderStatus } from "@/types/order.types";

import { OrderFormData } from "./types";

type Props = {
  messages: OrderFormPageMessages;
  isEditing?: boolean;
};

export default function OrderFormPage({ messages, isEditing = false }: Props) {
  const { orderId } = useParams<{ orderId: string }>();
  const { user } = useAuth();
  const { lang } = useLanguage();
  const router = useNavigate();
  const { mutate } = useMutate();
  const { removePayment } = useRemovePayment();
  const { removeItem } = useRemoveItem();
  const { handleOrderStatusChange, isLoading: isLoadingStatus } = useHandleOrderStatusChange();
  const _id = Number(orderId) || 0;
  const { data: order, isLoading: isLoadingOrder, error } = useGetOrderById({ _id });
  const { update } = useUpdateOrder();
  const { create } = useCreateOrder();
  const { isLoading, startLoading, endLoading } = useLoading();
  const { showToast } = useToast();

  const onError = () => endLoading();

  const onSuccess = async () => {
    await mutate(SWR_KEYS.getOrderById);
    await mutate(SWR_KEYS.getOrders);
    endLoading();
  };

  const removePaymentFromDatabase = async (selected: IOrderPayment) => {
    if (!order) return;

    const orderId = Number(order.id);
    const paymentId = Number(selected.id);
    const successMessage = messages.paymentRemovedSuccess;
    const errorMessage = messages.paymentRemovedError;
    await removePayment({ orderId, paymentId, successMessage, errorMessage, onSuccess });
  };

  const removeItemFromDatabase = async (selected: IOrderItem) => {
    if (!order) return;

    const orderId = Number(order.id);
    const itemId = Number(selected.id);

    const successMessage = messages.successRemoveItemMessage;
    const errorMessage = messages.errorRemoveItemMessage;

    await removeItem({ orderId, itemId, successMessage, errorMessage });
  };

  const handleUpdate = async (_formData: OrderFormData) => {
    if (!order) return;

    startLoading();

    try {
      const { netTotal, couponDiscount } = getOrderItemTotals(_formData.items);
      _formData = {
        ..._formData,
        orderAmount: netTotal,
        discountCouponAmount: couponDiscount,
        totalAmount: getExpectedOrderAmount({
          ..._formData,
          orderAmount: netTotal,
          discountCouponAmount: couponDiscount,
        }),
      };

      if (!_formData.orderAmount || _formData.orderAmount <= 0) {
        showToast({ text: messages.orderAmountMustNotBeEmpty, type: "error" });
        return;
      }

      // Verificação de campos obrigatórios com base no status do pedido
      const validateRequiredFieldsByStatus = () => {
        switch (_formData.orderStatus) {
          case OrderStatus.Delivered:
            if (!_formData.deliveryDate) {
              showToast({ text: messages.errorDeliveryDateRequired, type: "error" });
              return false;
            }
            break;

          case OrderStatus.Invoiced:
            if (
              !_formData.invoiceKey ||
              !_formData.invoiceNumber ||
              !_formData.invoiceSeries ||
              !_formData.invoiceXml
            ) {
              showToast({ text: messages.errorInvoiceFieldsRequired, type: "error" });
              return false;
            }
            break;

          case OrderStatus.Error:
            if (!_formData.orderComments1 || String(_formData.orderComments1).trim().length === 0) {
              showToast({ text: messages.errorRequiredOrderStatus, type: "error" });
              return false;
            }
            if (String(_formData.orderComments1).length > 100) {
              showToast({ text: messages.orderUpdateError, type: "error" });
              return false;
            }
            break;

          default:
            return true;
        }
        return true;
      };

      if (!validateRequiredFieldsByStatus()) {
        endLoading();
        return;
      }

      const normalizedFormData = normalizeUpdate(_formData, order);
      const isStatusChanged = _formData.orderStatus !== order.orderStatus;
      const canUpdateOrder = [OrderStatus.Error, OrderStatus.Waiting].includes(order.orderStatus);

      const removalPromises = [];

      if (_formData.removedPayments && _formData.removedPayments.length > 0) {
        for (const payment of _formData.removedPayments) {
          removalPromises.push(removePaymentFromDatabase(payment));
        }
      }

      if (_formData.removedItems && _formData.removedItems.length > 0) {
        for (const item of _formData.removedItems) {
          removalPromises.push(removeItemFromDatabase(item));
        }
      }

      await Promise.all(removalPromises);

      const formData = {
        ...order,
        ...normalizedFormData,
        orderStatus: order.orderStatus,
      };
      const successMessage = messages.orderUpdateSuccess;
      const errorMessage = messages.orderUpdateError;
      const _id = Number(order.id) || 0;

      if (canUpdateOrder) {
        await update({
          _id,
          formData,
          successMessage: "",
          errorMessage,
          onError,
          onSuccess,
        });
      }

      if (isStatusChanged) {
        await handleOrderStatusChange(_formData, messages);
      }

      if (canUpdateOrder || isStatusChanged) {
        showToast({ text: successMessage, type: "success" });
        router(ROUTE_URLS.orders(lang));
      }
    } catch (error) {
      showToast({ text: messages.errorDuringRemovalOrUpdate, type: "error" });
      router(ROUTE_URLS.orders(lang));
    } finally {
      endLoading();
    }
  };

  const handleCreate = async (_formData: OrderFormData) => {
    startLoading();

    const successMessage = messages.orderCreateSuccess;
    const errorMessage = messages.orderCreateError;

    const formData = normalizeCreation(_formData);

    const order = await create({ successMessage, formData, errorMessage, onError });

    if (order && order.orderStatus !== _formData.orderStatus) {
      const updatedOrder = { ...order, orderStatus: _formData.orderStatus };
      await handleOrderStatusChange(updatedOrder, messages);
    }

    endLoading();
    showToast({ text: messages.orderCreateSuccess, type: "success" });
    router(ROUTE_URLS.orders(lang), { state: { showWaiting: true } });
  };

  const handleSubmit = (_formData: OrderFormData) => {
    if (!user) return;

    if (isEditing) handleUpdate(_formData);
    if (!isEditing) handleCreate(_formData);
  };

  const BREAD_CRUMBS = [{ title: messages.orderBreadCrumbTitle, href: ROUTE_URLS.orders(lang) }];
  const pageTitle = isEditing ? messages.editTitle : messages.createTitle;
  const shouldLoadError = !order && !!error && !isLoadingOrder;

  return (
    <PageLayout messages={messages} title={pageTitle} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        <Suspense fallback={<CircularProgress />} isSuspended={isLoadingOrder}>
          <OrderForm
            order={order}
            isEditing={isEditing}
            messages={messages}
            onSubmit={handleSubmit}
            isLoading={isLoading || isLoadingStatus}
          />

          {!!shouldLoadError && (
            <MDBox mt={2}>
              <Card>
                <AlertBlock
                  title={messages.notFoundError}
                  description={error.message}
                  alt={messages.notFoundAlt}
                />
              </Card>
            </MDBox>
          )}
        </Suspense>
      </PageContainer>
    </PageLayout>
  );
}
